import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('component.user.activity.dashboard');
import { AfterViewInit, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import Chart from 'chart.js/auto';
import { Service } from "src/libs/portal/season/service";

@Component({
    selector: 'wiz-component-user-activity-dashboard',
template: templateSource || '',
    styles: [`

/* file: /opt/app/project/main/build/src/app/component.user.activity.dashboard/view.scss */
:host {
  display: block;
}`],
})
export class ComponentUserActivityDashboardComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    private static nextComponentId: number = 0;

    @Input() dashboard: any = null;
    @Input() loading: boolean = false;
    @Input() user: any = null;
    @Input() showUserHeader: boolean = true;
    @Input() showGuideButton: boolean = true;
    @Input() exportBusy: boolean = false;
    @Output() weekMove = new EventEmitter<number>();
    @Output() weekReset = new EventEmitter<void>();
    @Output() exportRequest = new EventEmitter<void>();

    public componentId: string = `user-activity-dashboard-${++Component.nextComponentId}`;
    public guideOpen: boolean = false;
    public dashboardSeries: any[] = [
        { key: 'review_count', label: '리뷰', color: '#fb7185' },
        { key: 'activity_count', label: '작업이력', color: '#f59e0b' },
        { key: 'codex_count', label: '에이전트 요청', color: '#6366f1' }
    ];
    public chartSelection: any = { daily: '', hourly: '' };

    private chartInstances: any = {};
    private chartRenderTimer: any = null;

    constructor(@Inject( Service) public service: Service) { }

    public async ngOnInit() {
        await this.service.init();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.dashboard || changes.loading) {
            this.scheduleChartRender();
        }
    }

    public ngAfterViewInit() {
        this.scheduleChartRender();
    }

    public ngOnDestroy() {
        if (this.chartRenderTimer) {
            clearTimeout(this.chartRenderTimer);
            this.chartRenderTimer = null;
        }
        this.destroyAllCharts();
    }

    public data() {
        return this.dashboard || this.emptyDashboard();
    }

    public emptyDashboard() {
        return {
            user: { id: '', name: '', email: '', role: '', account_status_label: '', signup_provider: '', last_login_at: '', created: '' },
            period: { start: '', end: '', label: '', previous_start: '', next_start: '', current_start: '', is_current: true, timezone: 'UTC+9' },
            totals: { reviews: 0, activities: 0, codex_requests: 0, codex_duration_seconds: 0, codex_duration_label: '0분', active_days: 0, active_hours: 0, projects: 0, touched_reviews: 0 },
            chart: { days: [], hours: [], max_count: 1, hourly_max_count: 1, timezone: 'UTC+9' },
            distributions: { statuses: [], categories: [] },
            rankings: { projects: [] },
            productivity: { score: 0, metrics: [], days: [], baselines: {}, formula: '' },
            evaluation: { score: 0, metrics: [], methodology: [] },
            activity: { weekly: { score: 0 }, monthly: { score: 0 } }
        };
    }

    public displayUser() {
        return this.data().user || this.user || {};
    }

    public displayUserName() {
        const user = this.displayUser();
        return user.name || user.display_name || user.email || '사용자';
    }

    public displayUserMeta() {
        const user = this.displayUser();
        return [
            user.email || '',
            this.roleLabel(user.role || ''),
            user.account_status_label || ''
        ].filter((value: any) => !!value).join(' · ') || '-';
    }

    public roleLabel(role: string) {
        const labels: any = {
            admin: '관리자',
            internal: '내부사용자',
            user: '사용자',
            guest: '손님'
        };
        return labels[role || 'user'] || role;
    }

    public async openGuide() {
        this.guideOpen = true;
        await this.service.render();
    }

    public async closeGuide() {
        this.guideOpen = false;
        await this.service.render();
    }

    public moveWeek(direction: number) {
        this.weekMove.emit(direction);
    }

    public resetWeek() {
        this.weekReset.emit();
    }

    public exportHistory() {
        if (this.exportBusy) return;
        this.exportRequest.emit();
    }

    public period() {
        return this.data().period || {};
    }

    public totals() {
        return this.data().totals || {};
    }

    public dashboardDays() {
        const chart = this.data().chart || {};
        return Array.isArray(chart.days) ? chart.days : [];
    }

    public dashboardHours() {
        const chart = this.data().chart || {};
        return Array.isArray(chart.hours) ? chart.hours : [];
    }

    public dashboardRanking(kind: string) {
        const rankings = this.data().rankings || {};
        return Array.isArray(rankings[kind]) ? rankings[kind] : [];
    }

    public topProjects() {
        return this.dashboardRanking('projects').slice(0, 10);
    }

    public distribution(kind: string) {
        const distributions = this.data().distributions || {};
        return Array.isArray(distributions[kind]) ? distributions[kind] : [];
    }

    public topDistribution(kind: string) {
        return this.distribution(kind).slice(0, 5);
    }

    public distributionEmpty(kind: string) {
        return this.distribution(kind).length === 0;
    }

    public rankEmpty(kind: string) {
        return this.dashboardRanking(kind).length === 0;
    }

    public productivity() {
        return this.data().productivity || { score: 0, metrics: [], days: [], baselines: {}, formula: '' };
    }

    public productivityMetrics() {
        const productivity = this.productivity();
        return Array.isArray(productivity.metrics) ? productivity.metrics : [];
    }

    public productivityDays() {
        const productivity = this.productivity();
        return Array.isArray(productivity.days) ? productivity.days : [];
    }

    public evaluationMetrics() {
        const evaluation = this.data().evaluation || {};
        return Array.isArray(evaluation.metrics) ? evaluation.metrics : [];
    }

    public activityMetricCards() {
        const weekly = this.activityPeriodSummary('weekly');
        if (weekly && Array.isArray(weekly.metrics) && weekly.metrics.length) {
            return weekly.metrics;
        }
        const productionMetrics = this.productivityMetrics().map((metric: any) => Object.assign({ group: 'production' }, metric));
        const evaluationMetrics = this.evaluationMetrics().map((metric: any) => Object.assign({ group: 'evaluation' }, metric));
        return productionMetrics.concat(evaluationMetrics);
    }

    public activityScore() {
        const weekly = this.activityPeriodSummary('weekly');
        return Number(weekly && weekly.score != null ? weekly.score : 0);
    }

    public activityDays() {
        return this.productivityDays();
    }

    public activityPeriodSummary(kind: string) {
        const activity = this.data().activity || {};
        return activity && activity[kind] ? activity[kind] : { label: kind, period_label: '', score: 0, totals: {} };
    }

    public metricLabel(metric: any) {
        const key = metric && metric.key ? metric.key : '';
        const labels: any = {
            qa_productivity: 'QA 생산성',
            issue_response: '이슈대응력',
            ai_utilization: 'AI 활용',
            review_quality: '리뷰 품질',
            activity_quality: '이슈 품질',
            issue_effort: '이슈 노력',
            multitasking: '멀티태스킹',
            activity_consistency: '지속성'
        };
        return labels[key] || (metric && metric.label ? metric.label : '-');
    }

    public metricIcon(metric: any) {
        const key = metric && metric.key ? metric.key : '';
        const icons: any = {
            qa_productivity: 'fa-solid fa-pen-to-square text-rose-500',
            issue_response: 'fa-solid fa-list-check text-amber-500',
            ai_utilization: 'fa-solid fa-terminal text-indigo-500',
            review_quality: 'fa-solid fa-circle-check text-emerald-500',
            activity_quality: 'fa-solid fa-arrows-rotate text-cyan-500',
            issue_effort: 'fa-solid fa-code-branch text-sky-500',
            multitasking: 'fa-solid fa-layer-group text-violet-500',
            activity_consistency: 'fa-solid fa-calendar-check text-slate-500'
        };
        return icons[key] || 'fa-solid fa-chart-simple text-slate-500';
    }

    public metricSummary(metric: any) {
        if (!metric) return '-';
        if (metric.summary) return metric.summary;
        if (metric.group === 'production') return `총 ${this.dashboardCount(metric.count)}건`;
        return metric.inputs || '-';
    }

    public metricDetail(metric: any) {
        if (!metric) return '';
        if (metric.detail) return metric.detail;
        if (metric.group === 'production') return `기준 ${this.dashboardCount(metric.baseline_count)}건/일`;
        return metric.method || '';
    }

    public guideItems() {
        return [
            {
                title: '현재 적용 산식',
                formula: '주간 점수 = 기간 누적량 / (90일 안정 기준 * KST 유효 일수)',
                description: '진행 중인 주/월은 KST 오늘까지의 날짜만 유효 일수에 넣고 미래 날짜는 제외합니다. 이미 지난 0건 날짜는 평균에 포함합니다.'
            },
            {
                title: '활동성 지표',
                formula: 'QA 30.7%(생산량 40 + 품질 보정 60) + 이슈 30.7%(작업량 25 + 품질 보정 60 + 노력 15) + AI 30.6% + 보조 8%',
                description: '작업량이 기본 점수이며, 품질은 작업량에 곱해지는 보정으로 반영해 적은 활동만으로 고점이 나오지 않게 합니다.'
            },
            {
                title: '주간/월간 지표',
                formula: '선택 주간 점수와 해당 월 누적 점수 병행 표시',
                description: '월간도 같은 누적 산식을 쓰며, 현재 월은 KST 오늘 이후 날짜를 유효 일수와 집계 범위에서 제외합니다.'
            },
            {
                title: '생산량 기준값',
                formula: 'max(최근 90일 사용자-일별 최대, P90, 상위 5개 사용자-일 평균)',
                description: '표본이 20개 미만이면 전체 기간을 사용합니다. 선택 주간의 상대 순위가 아니라 과거 기준값으로 분모를 고정합니다.'
            },
            {
                title: 'AI 활용',
                formula: '누적 에이전트 응답수 35% + 누적 작업시간 35% + 사용량 보정 요청 품질 30%',
                description: '요청 품질은 실제 AI 응답수나 작업시간 달성률이 있을 때만 비례 반영합니다. 후속 요청은 120자 기준으로 맥락을 평가합니다.'
            },
            {
                title: 'QA/이슈 통합',
                formula: 'QA = 생산량 40% + 생산량×리뷰품질 60%, 이슈 = 작업량 25% + 작업량×이슈품질 60% + 노력 15%',
                description: '의미 없는 리뷰/이슈 생성을 막기 위해 품질만 따로 점수를 주지 않고 실제 누적 작업량에 품질을 보정합니다.'
            },
            {
                title: '이슈 대응 노력',
                formula: '에이전트 고도화 100%',
                description: '리뷰당 에이전트 3회까지의 정의·수정·검증 노력을 점수화하되, 후속 질문은 구체적인 맥락을 담아야 AI 활용에도 유리합니다.'
            },
            {
                title: '멀티태스킹',
                formula: '활동 프로젝트 수 / 10개 * 100',
                description: '선택 기간에 실제 활동이 발생한 프로젝트 수로 업무 병행 범위를 점수화합니다.'
            },
            {
                title: '지속성',
                formula: '기간 대비 활동일 40 + 시간대 25 + 리뷰 폭 35',
                description: '점수가 높게 나오기 쉬운 보조 성격을 고려해 종합 반영 비중은 4%로 낮춥니다.'
            }
        ];
    }

    public dashboardScore(value: any) {
        const score = Math.max(0, Math.min(100, Number(value || 0)));
        return score.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
    }

    public dashboardScoreBarWidth(value: any) {
        return `${Math.max(4, Math.min(100, Number(value || 0)))}%`;
    }

    public dashboardCount(value: any) {
        const count = Math.max(0, Number(value || 0));
        return count.toLocaleString('ko-KR');
    }

    public dashboardRankBarWidth(item: any) {
        return `${Math.max(4, Math.min(100, Number(item && item.percent ? item.percent : 0)))}%`;
    }

    public distributionBarWidth(item: any) {
        return `${Math.max(4, Math.min(100, Number(item && item.percent ? item.percent : 0)))}%`;
    }

    public projectActivitySegmentWidth(item: any, key: string) {
        const total = Math.max(0, Number(item && item.count ? item.count : 0));
        const value = Math.max(0, Number(item && item[key] ? item[key] : 0));
        if (!total || !value) return '0%';
        return `${(value / total) * 100}%`;
    }

    public async toggleChartSeries(scope: string, key: string) {
        const selected = this.chartSelection[scope] || '';
        this.chartSelection[scope] = selected === key ? '' : key;
        await this.service.render();
        this.scheduleChartRender();
    }

    public isChartSeriesVisible(scope: string, key: string) {
        const selected = this.chartSelection[scope] || '';
        return !selected || selected === key;
    }

    public visibleSeries(scope: string) {
        return this.dashboardSeries.filter((series: any) => this.isChartSeriesVisible(scope, series.key));
    }

    public chartLegendPressed(scope: string, key: string) {
        return this.chartSelection[scope] === key ? 'true' : 'false';
    }

    public chartLegendClass(scope: string, key: string) {
        const active = this.isChartSeriesVisible(scope, key);
        const base = 'inline-flex h-8 items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-semibold transition';
        return active
            ? `${base} border-slate-300 bg-white text-slate-900 shadow-sm`
            : `${base} border-slate-200 bg-slate-100 text-slate-400 hover:border-slate-300 hover:text-slate-600`;
    }

    public chartCanvasId(kind: string) {
        return `${this.componentId}-${kind}-chart`;
    }

    private scheduleChartRender() {
        if (this.chartRenderTimer) {
            clearTimeout(this.chartRenderTimer);
        }
        this.chartRenderTimer = setTimeout(() => {
            this.chartRenderTimer = null;
            this.renderCharts();
        }, 0);
    }

    private chartCanvas(id: string) {
        if (typeof document === 'undefined') return null;
        return document.getElementById(id) as HTMLCanvasElement | null;
    }

    private renderCharts() {
        if (this.loading) {
            this.destroyAllCharts();
            return;
        }
        this.renderLineChart('daily', this.chartCanvasId('daily'), this.dashboardDays(), 'daily', 'daily');
        this.renderLineChart('hourly', this.chartCanvasId('hourly'), this.dashboardHours(), 'hourly', 'hourSlots');
    }

    private destroyChart(key: string) {
        if (!this.chartInstances[key]) return;
        try {
            this.chartInstances[key].destroy();
        } catch (e) { }
        delete this.chartInstances[key];
    }

    private destroyAllCharts() {
        Object.keys(this.chartInstances).forEach((key: string) => this.destroyChart(key));
    }

    private chartLabels(items: any[], mode: string) {
        const rows = Array.isArray(items) ? items : [];
        if (mode === 'daily') {
            return rows.map((item: any) => [item.label || '', String(item.date || '').slice(5)]);
        }
        return rows.map((item: any) => `${String(item.date || '').slice(5)} ${item.label || '00'}시`);
    }

    private chartTitle(items: any[], mode: string, index: number) {
        const item = Array.isArray(items) ? items[index] : null;
        if (!item) return '';
        if (mode === 'daily') return `${item.date || ''} ${item.label || ''}`;
        return `${item.date || ''} ${item.label || '00'}시`;
    }

    private chartColor(hex: string, alpha: number) {
        const value = String(hex || '').replace('#', '');
        if (value.length !== 6) return `rgba(15, 23, 42, ${alpha})`;
        const r = parseInt(value.slice(0, 2), 16);
        const g = parseInt(value.slice(2, 4), 16);
        const b = parseInt(value.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    private renderLineChart(key: string, canvasId: string, items: any[], scope: string, mode: string) {
        const canvas = this.chartCanvas(canvasId);
        if (!canvas) {
            this.destroyChart(key);
            return;
        }
        const rows = Array.isArray(items) ? items : [];
        const labels = this.chartLabels(rows, mode);
        const datasets = this.visibleSeries(scope).map((series: any) => ({
            label: series.label,
            data: rows.map((item: any) => Math.max(0, Number(item && item[series.key] ? item[series.key] : 0))),
            borderColor: series.color,
            backgroundColor: this.chartColor(series.color, 0.12),
            borderWidth: mode === 'hourSlots' ? 2 : 2.4,
            pointRadius: rows.length > 48 ? 0 : 3,
            pointHoverRadius: 4,
            pointBorderWidth: 1.5,
            tension: 0.34,
            fill: true,
        }));

        this.destroyChart(key);
        this.chartInstances[key] = new Chart(canvas, {
            type: 'line',
            data: { labels, datasets },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            title: (items: any[]) => {
                                const index = items && items.length ? items[0].dataIndex : 0;
                                return this.chartTitle(rows, mode, index);
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            color: '#64748b',
                            font: { size: 11, weight: '600' },
                            maxRotation: 0,
                            autoSkip: mode !== 'hourSlots',
                            maxTicksLimit: mode === 'hourSlots' ? 8 : 12,
                            callback: (value: any, index: number) => {
                                if (mode !== 'hourSlots') return labels[index] as any;
                                const item = rows[index] || {};
                                if (index % 24 === 0 || index === rows.length - 1) {
                                    return item.tick_label || String(item.date || '').slice(5);
                                }
                                return '';
                            },
                        },
                    },
                    y: {
                        beginAtZero: true,
                        grid: { color: '#e2e8f0' },
                        ticks: {
                            color: '#64748b',
                            precision: 0,
                            callback: (value: any) => this.dashboardCount(value),
                        },
                    },
                },
            },
        });
    }
}

export default ComponentUserActivityDashboardComponent;